import {
  default as Button,
  default as CustomButton,
} from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import WorkflowStatus from "@/components/WorkflowStatus";
import { capitalizeText } from "@/lib/capitalizeText";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { sanitizeSdReturnTo } from "@/lib/sanitizeSdReturnTo";
import { normalizeDynamicModuleUploadFields } from "@/lib/normalizeDynamicModuleUploadFields";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useA1ResolvedNav } from "@/hooks/useA1ResolvedNav";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { Card } from "antd";
import { Printer, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const ModuleFormPage: React.FC = () => {
  const navigationRoutes = useA1ResolvedNav();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnToSd = useMemo(
    () => sanitizeSdReturnTo(searchParams.get("returnTo")),
    [searchParams]
  );

  const { schemaName = "", id } = useParams<{
    schemaName: string;
    id: string;
  }>();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useMemo(() => createForm({}), []);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName),
    enabled: !!schemaName,
  });

  const { data: moduleInfo, isLoading: moduleLoading } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName, id ?? ""),
    enabled: isEditMode && !!schemaName && !!id,
  });

  const handleSubmit = async () => {
    await form.validate();
    let modifiedValues = removeMetaFields(form.values) as Record<
      string,
      unknown
    >;
    modifiedValues = normalizeDynamicModuleUploadFields(
      schemaInfo?.json ?? schemaInfo,
      modifiedValues
    );
    setIsSubmitting(true);

    if (isEditMode && moduleInfo) {
      const originalData = removeMetaFields(moduleInfo);
      if (!checkFormChanges(modifiedValues, originalData, notify)) {
        setIsSubmitting(false);
        return;
      }
    }

    if (isEditMode) {
      const response = await DynamicModuleService.updateItem(
        schemaName,
        id!,
        modifiedValues
      );
      if (response) {
        navigate(returnToSd ?? navigationRoutes.MODULE(schemaName ?? ""));
      }
    } else {
      const response = await DynamicModuleService.createItem(
        schemaName,
        modifiedValues
      );
      if (response) {
        navigate(returnToSd ?? navigationRoutes.MODULE(schemaName ?? ""));
      }
    }
    setIsSubmitting(false);
  };
  useEffect(() => {
    if (isEditMode && moduleInfo) {
      form.setInitialValues(removeMetaFields(moduleInfo));
    }
  }, [isEditMode, moduleInfo, form]);

  if (isEditMode && moduleLoading) {
    return <LoadingSpinner />;
  }
  if (isEditMode && !moduleInfo) {
    return <NotFound entityName="Module" />;
  }
  if (schemaLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            <div className="flex w-full items-center justify-between gap-2.5">
              <span className="text-lg font-semibold">
                {isEditMode
                  ? `Edit ${capitalizeText(schemaName)}`
                  : `Add ${capitalizeText(schemaName)}`}
              </span>

              {isEditMode && (
                <WorkflowStatus schemaName={schemaName} recordId={id ?? ""} />
              )}
            </div>
          }
          extra={
            <div className="flex items-center gap-3">
              {isEditMode && (
                <Button
                  type="primary"
                  onClick={() =>
                    navigate(navigationRoutes.PRINT(schemaName ?? "", id ?? ""))
                  }
                >
                  <Printer size={16} />
                </Button>
              )}
              <CustomButton
                type="primary"
                icon={<Save size={16} />}
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
                size="middle"
                title={isEditMode ? "Save Record Changes" : "Save New Record"}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditMode
                    ? "Save Changes"
                    : "Save"}
              </CustomButton>
            </div>
          }
          onBack={() =>
            navigate(returnToSd ?? navigationRoutes.MODULE(schemaName))
          }
        />
      }
    >
      <SchemaFormView form={form} schemaJson={schemaInfo?.json ?? schemaInfo} />
    </Card>
  );
};

export default ModuleFormPage;
